import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { dateToStoreISO } from '../utils/timezones';
import { DateRangeField } from './DateRangeField';
import { getErrorMessage } from '../api/client';
import { getOwnerDashboardSummary, getPendingSummary } from '../api/dashboard';
import { useAuth } from '../auth/AuthProvider';
import { getFinancialStoreOptions } from '../auth/permissions';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { CheckRow } from './ui/CheckRow';
import type {
  DashboardAlert,
  DashboardAlertSeverity,
  DashboardDailyTrendItem,
  DashboardStoreBreakdownItem,
  DashboardTopProductItem,
  OwnerDashboardSummary,
  PendingSummary,
} from '../types/dashboard';
import { formatMoney, formatOptionalMoney, formatOptionalSignedMoney, formatQuantity, RealCostBadge, RecipeStatusBadge } from '../utils/costFormatting';
import { useLoyversePresence } from '../utils/loyversePresence';

interface DraftFilters {
  dateFrom: string;
  dateTo: string;
  storeId: string;
  comparePreviousPeriod: boolean;
}

function defaultDraft(preselectedStoreId = ''): DraftFilters {
  const today = new Date();
  const dateTo = dateToStoreISO(today);
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { dateFrom: dateToStoreISO(from), dateTo, storeId: preselectedStoreId, comparePreviousPeriod: true };
}

/**
 * Owner Intelligence v1 — la pantalla analítica de siempre, ÍNTEGRA, ahora bajo "Ver detalle"
 * (spec §10, §22). No se borró ni una cifra: es una herramienta legítima para quien sabe leerla, y
 * desmontarla perdería trabajo real y probado.
 *
 * Lo que sí cambió: dejó de ser la primera pantalla que ve el dueño, y cada cifra conservada
 * declara su BASE DE CÁLCULO (todos los canales / solo Loyverse / solo líneas costeadas). Ese
 * etiquetado es obligatorio — hoy "Venta total" y "Margen real promedio" convivían en la misma
 * rejilla, con el mismo peso visual, calculados sobre bases distintas y sin decirlo.
 */
export function OwnerDashboardDetail({ onNavigate }: { onNavigate: (destination: string) => void }) {
  const { user, stores: authStores } = useAuth();
  // Fase 16B-2 (Fase 16B-1, brecha 2): un OWNER puede pedir el agregado de todo el negocio; un
  // MANAGER debe elegir una tienda concreta con `canViewFinancials: true` — el backend ya lo exige
  // (`FINANCIALS_PERMISSION_REQUIRED`), aquí solo se evita ofrecer una opción que siempre fallaría.
  const financialStores = getFinancialStoreOptions(user?.role, authStores);
  const preselected = !financialStores.allowsAllStores && financialStores.selectableStores.length === 1
    ? financialStores.selectableStores[0]!.id
    : '';
  const [draft, setDraft] = useState<DraftFilters>(() => defaultDraft(preselected));
  const [summary, setSummary] = useState<OwnerDashboardSummary>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async (next: DraftFilters) => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getOwnerDashboardSummary({
        dateFrom: next.dateFrom,
        dateTo: next.dateTo,
        storeId: next.storeId || undefined,
        comparePreviousPeriod: next.comparePreviousPeriod,
        includeAlerts: true,
        includeStoreBreakdown: true,
        includeTopProducts: true,
        includeDailyTrend: true,
      });
      setSummary(result);
    } catch (loadError) {
      setSummary(undefined);
      setError(getErrorMessage(loadError, 'No se pudo cargar el dashboard. Intenta de nuevo.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (financialStores.hasNoFinancialAccess) {
      setIsLoading(false);
      return;
    }
    void load(defaultDraft(preselected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (financialStores.hasNoFinancialAccess) {
    return <AccessDeniedPage
      message="Tu usuario no tiene acceso a la información financiera de estas tiendas. Si crees que deberías verla, habla con el dueño del negocio."
      onReturn={() => onNavigate('dashboard')}
      title="No tienes permiso para ver los Reportes del negocio"
    />;
  }

  const rangeIsValid = Boolean(draft.dateFrom) && Boolean(draft.dateTo) && draft.dateFrom <= draft.dateTo;
  const storeIsValid = financialStores.allowsAllStores || Boolean(draft.storeId);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!rangeIsValid || !storeIsValid || isLoading) return;
    void load(draft);
  };

  const storeName = financialStores.selectableStores.find((store) => store.id === draft.storeId)?.name ?? null;
  const criticalAlertsCount = summary?.alerts.filter((alert) => alert.severity === 'CRITICAL').length ?? 0;
  // ZTV-02/ZTV-04: el resumen principal es neutral al canal; las secciones que SOLO saben de ventas
  // conectadas desde Loyverse (costos por ticket, productos, tendencia, desglose) se muestran
  // únicamente cuando la integración está conectada (o aún no se sabe: jamás se le quitan a un
  // negocio con Loyverse por un parpadeo) y se etiquetan con su canal.
  const loyverseConnected = useLoyversePresence();
  const showLoyverseSections = loyverseConnected !== false;
  const hasBothChannels = summary ? Number(summary.sales.grossSales) > 0 && Number(summary.sales.directSalesTotal) > 0 : false;

  return <div className="owner-dashboard-page">
    <p className="consumption-preview-intro">Análisis con calma: ventas, margen, inventario y cortes del rango que elijas. Solo lectura: no modifica ventas ni inventario.</p>

    <section className="preview-section owner-dashboard-filters-section">
      <div className="preview-section__heading"><div><h3>Filtros</h3><span>{financialStores.allowsAllStores ? 'Elige un rango de fechas y, si quieres, una tienda específica.' : 'Elige un rango de fechas y una tienda (solo se muestran las tuyas con permiso financiero).'}</span></div></div>
      <form className="stock-filters" onSubmit={submit}>
        <DateRangeField allowClear={false} from={draft.dateFrom} onChange={(from, to) => setDraft({ ...draft, dateFrom: from, dateTo: to })} to={draft.dateTo} />
        <label className="field"><span>Tienda</span><select onChange={(event) => setDraft({ ...draft, storeId: event.target.value })} value={draft.storeId}>{financialStores.allowsAllStores && <option value="">Todas las tiendas</option>}{!financialStores.allowsAllStores && !draft.storeId && <option value="">Selecciona una tienda</option>}{financialStores.selectableStores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
        {/* P-076 (P5): casilla con objetivo táctil de 44 px pegado a su etiqueta. */}
        <CheckRow checked={draft.comparePreviousPeriod} label="Comparar con periodo anterior" onChange={(checked) => setDraft({ ...draft, comparePreviousPeriod: checked })} plain />
        <button className="button button--secondary" disabled={isLoading || !rangeIsValid || !storeIsValid} type="submit">{isLoading ? 'Actualizando...' : 'Actualizar reporte'}</button>
      </form>
      {!rangeIsValid && <div className="alert alert--warning">Revisa las fechas seleccionadas: "Hasta" debe ser igual o posterior a "Desde".</div>}
      {rangeIsValid && !storeIsValid && <div className="alert alert--warning">Selecciona una tienda válida para ver su información financiera.</div>}
    </section>

    {error && <div className="alert alert--error">{error}<div><button className="button button--ghost" onClick={() => void load(draft)} type="button">Reintentar</button></div></div>}
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando dashboard...</div>}

    {!isLoading && !error && !summary && <div className="empty-panel"><strong>No hay datos para este periodo.</strong><span>Intenta con otro rango de fechas o tienda.</span></div>}

    {!isLoading && summary && <>
      {summary.sales.totalTransactions === 0 && <div className="alert alert--warning">No hay ventas registradas en este periodo.</div>}

      <section className="preview-section owner-dashboard-summary-section">
        <div className="preview-section__heading"><div><h3>Resumen principal</h3><span>{storeName ?? 'Todas las tiendas'} · {summary.period.dateFrom.slice(0, 10)} a {summary.period.dateTo.slice(0, 10)}</span></div></div>
        {/* Cada tarjeta declara SOBRE QUÉ está calculada. Sin esta línea, "Venta total" (todos los
            canales) y "Margen real promedio" (solo Loyverse, solo líneas costeadas) se leen como
            si fueran comparables — y el dueño concluye que gana el triple. */}
        <div className="metric-grid owner-dashboard-metric-grid">
          <MetricCard basis={BASIS_ALL} help="Todas las ventas del negocio en el periodo: registradas aquí y conectadas desde Loyverse." label="Venta total" value={formatMoney(summary.sales.totalSales)} tone="orange" />
          <MetricCard basis={BASIS_ALL} help="Número de ventas del periodo, de todos los canales." label="Ventas" value={String(summary.sales.totalTransactions)} tone="slate" />
          <MetricCard basis={BASIS_ALL} help="Venta total entre número de ventas." label="Venta promedio" value={formatMoney(summary.sales.averageSale)} tone="slate" />
          <MetricCard basis={BASIS_COSTED} help="Venta menos costo real promedio." label="Te quedó después del costo" value={formatOptionalMoney(summary.costs.realAverageMargin)} tone="green" />
          <MetricCard basis={BASIS_COSTED} help="De cada $100 que vendes, cuánto te queda después del costo de lo vendido." label="De cada $100, te quedan" value={formatOptionalPercent(summary.costs.realAverageMarginPercent)} tone="green" />
          <MetricCard basis={BASIS_SHIFTS} help="Efectivo contado menos efectivo esperado en cortes de turno." label="Diferencia de efectivo" value={`${formatOptionalMoney(summary.shiftCloses.cashDifference)} · ${cashDifferenceLabel(summary.shiftCloses.cashDifference)}`} tone={cashDifferenceTone(summary.shiftCloses.cashDifference)} />
          <MetricCard label="Alertas críticas" value={String(criticalAlertsCount)} tone={criticalAlertsCount > 0 ? 'orange' : 'slate'} />
          <MetricCard basis={BASIS_COSTED} label="Costo estimado" value={formatOptionalMoney(summary.costs.estimatedCost)} tone="slate" />
          <MetricCard basis={BASIS_COSTED} help="Lo que te costó preparar lo que vendiste, según tus compras confirmadas." label="Costo real promedio" value={formatOptionalMoney(summary.costs.realAverageCost)} tone="slate" />
          <MetricCard basis={BASIS_COSTED} label="Margen estimado" value={formatOptionalMoney(summary.costs.estimatedMargin)} tone="slate" />
          <MetricCard basis={BASIS_COSTED} label="Margen estimado %" value={formatOptionalPercent(summary.costs.estimatedMarginPercent)} tone="slate" />
        </div>
        <p className="basis-note">
          "Te quedó después del costo" está calculado solo sobre las líneas de venta cuyo costo se
          conoce. Todavía sin restar renta, sueldos ni servicios: no es tu ganancia.
        </p>
        {(hasBothChannels || (showLoyverseSections && summary.sales.directSalesCount > 0)) && (
          <p className="consumption-preview-intro">
            Por canal: conectadas desde Loyverse {formatMoney(summary.sales.grossSales)} ({summary.sales.totalReceipts}) · registradas aquí {formatMoney(summary.sales.directSalesTotal)} ({summary.sales.directSalesCount}).
          </p>
        )}
      </section>

      {showLoyverseSections && <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading">
          <div><h3>Ventas y margen</h3><span>Comparativo entre lo estimado y lo real. Costos y márgenes se calculan sobre las ventas conectadas desde Loyverse.</span></div>
          <button className="button button--ghost" onClick={() => onNavigate('inventory/consumption')} type="button">Ver simulación por ventas</button>
        </div>
        <div className="coverage-summary cost-summary">
          <SummaryCard label="Venta bruta (Loyverse)" value={formatMoney(summary.sales.grossSales)} />
          <SummaryCard label="Venta neta (Loyverse)" value={formatMoney(summary.sales.netSales)} />
          <SummaryCard help="Basado en el precio de compra de referencia de cada insumo." label="Costo estimado" value={formatOptionalMoney(summary.costs.estimatedCost)} />
          <SummaryCard help="Basado en compras confirmadas y stock." label="Costo real promedio" value={formatOptionalMoney(summary.costs.realAverageCost)} />
          <SummaryCard label="Margen estimado" value={formatOptionalMoney(summary.costs.estimatedMargin)} />
          <SummaryCard help="Venta menos costo real promedio." label="Margen real promedio" value={formatOptionalMoney(summary.costs.realAverageMargin)} />
          <SummaryCard label="Margen real %" value={formatOptionalPercent(summary.costs.realAverageMarginPercent)} />
          <SummaryCard label="Diferencia costo estimado vs. real" value={formatOptionalSignedMoney(summary.costs.plannedVsRealDifference)} />
        </div>
      </section>}

      <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading"><div><h3>Alertas operativas</h3><span>Elementos que pueden requerir revisión antes de tomar decisiones.</span></div></div>
        {summary.alerts.length === 0
          ? <div className="empty-panel empty-panel--small"><strong>No hay alertas importantes para este periodo.</strong></div>
          : <div className="alerts-grid">{summary.alerts.map((alert, index) => <AlertCard alert={alert} key={index} />)}</div>}
      </section>

      {showLoyverseSections && <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading"><div><h3>Desglose por tienda</h3><span>Venta, costo, margen y diferencia de efectivo por tienda. Solo ventas conectadas desde Loyverse.</span></div></div>
        <StoreBreakdownTable rows={summary.storeBreakdown} />
      </section>}

      {showLoyverseSections && <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading">
          <div><h3>Productos principales</h3><span>Los productos con mayor venta bruta en el periodo. Solo ventas conectadas desde Loyverse.</span></div>
          <button className="button button--ghost" onClick={() => onNavigate('recipes')} type="button">Ver recetas</button>
        </div>
        <TopProductsTable rows={summary.topProducts} />
      </section>}

      {showLoyverseSections && <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading"><div><h3>Tendencia diaria</h3><span>Venta bruta, tickets y margen por día. Solo ventas conectadas desde Loyverse.</span></div></div>
        <DailyTrendTable rows={summary.dailyTrend} />
      </section>}

      <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading">
          <div><h3>Cortes de turno</h3><span>Cómo cerraron los cortes de este periodo.</span></div>
          <button className="button button--ghost" onClick={() => onNavigate('shifts')} type="button">Ver cortes de turno</button>
        </div>
        {summary.shiftCloses.totalCloses === 0
          ? <div className="empty-panel empty-panel--small"><strong>No hay cortes de turno para este periodo.</strong></div>
          : <div className="consumption-summary" aria-label="Resumen de cortes de turno">
              <article><span>Cortes cerrados</span><strong>{summary.shiftCloses.activeCloses}</strong></article>
              <article><span>Cortes anulados</span><strong>{summary.shiftCloses.voidedCloses}</strong></article>
              <article><span>Venta bruta desde cortes</span><strong>{formatMoney(summary.shiftCloses.grossSalesFromCloses)}</strong></article>
              <article><span>Efectivo esperado</span><strong>{formatOptionalMoney(summary.shiftCloses.expectedCash)}</strong></article>
              <article><span>Efectivo contado</span><strong>{formatMoney(summary.shiftCloses.countedCash)}</strong></article>
              <article><span title="Efectivo contado menos efectivo esperado en cortes de turno.">Diferencia total</span><strong>{formatOptionalMoney(summary.shiftCloses.cashDifference)}</strong><small>{cashDifferenceLabel(summary.shiftCloses.cashDifference)}</small></article>
              <article><span>Cortes con diferencia</span><strong>{summary.shiftCloses.closesWithDifference}</strong></article>
            </div>}
      </section>

      <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading">
          <div><h3>Inventario</h3><span>Resumen del periodo, no el historial completo del inventario.</span></div>
          <button className="button button--ghost" onClick={() => onNavigate('inventory/stock')} type="button">Ver inventario</button>
        </div>
        {summary.inventory.stockItems === 0
          ? <div className="empty-panel empty-panel--small"><strong>No hay datos de inventario disponibles.</strong></div>
          : <div className="consumption-summary" aria-label="Resumen de inventario">
              <article><span>Insumos con stock</span><strong>{summary.inventory.stockItems}</strong></article>
              <article><span>Valor de stock (costo real)</span><strong>{formatMoney(summary.inventory.stockValueRealAverage)}</strong></article>
              <article><span>Stock bajo</span><strong>{summary.inventory.lowStockItems}</strong></article>
              <article><span>Sin stock</span><strong>{summary.inventory.noStockItems}</strong></article>
              <article><span>Compras</span><strong>{formatMoney(summary.inventory.purchaseInTotal)}</strong></article>
              <article><span>Mermas</span><strong>{formatMoney(summary.inventory.wasteOutTotal)}</strong>{Number(summary.inventory.wasteOutReversedTotal ?? 0) > 0 && <small>Neta de {formatMoney(summary.inventory.wasteOutReversedTotal!)} anulada</small>}{Number(summary.inventory.wasteDetectedInCountTotal ?? 0) > 0 && <small>Reportada {formatMoney(summary.inventory.wasteReportedTotal ?? '0')} · detectada en conteo {formatMoney(summary.inventory.wasteDetectedInCountTotal!)}</small>}</article>
              {/* F3 (D4): lo que faltó o sobró al contar y nadie explicó. No es merma. */}
              {summary.inventory.countShortageTotal !== undefined && (Number(summary.inventory.countShortageTotal) > 0 || Number(summary.inventory.countSurplusTotal ?? 0) > 0) && (
                <article><span>Diferencia de conteo sin explicar</span>
                  {Number(summary.inventory.countShortageTotal) > 0
                    ? <><strong>{formatMoney(summary.inventory.countShortageTotal)}</strong><small>Faltante{Number(summary.inventory.countSurplusTotal ?? 0) > 0 ? ` · sobrante ${formatMoney(summary.inventory.countSurplusTotal!)}` : ''}</small></>
                    : <><strong>{formatMoney(summary.inventory.countSurplusTotal ?? '0')}</strong><small>Sobrante</small></>}
                </article>
              )}
              <article><span>Salidas por venta</span><strong>{formatMoney(summary.inventory.saleOutTotal)}</strong></article>
              <article><span>Reversas de salida</span><strong>{formatMoney(summary.inventory.saleOutReversalTotal)}</strong></article>
              <article><span>Traspasos (entrada)</span><strong>{formatMoney(summary.inventory.transferInTotal)}</strong></article>
              <article><span>Traspasos (salida)</span><strong>{formatMoney(summary.inventory.transferOutTotal)}</strong></article>
            </div>}
      </section>

      <section className="preview-section owner-dashboard-section">
        <div className="preview-section__heading"><div><h3>Comparación contra periodo anterior</h3><span>Contra el periodo anterior de la misma duración.</span></div></div>
        <ComparisonSection comparison={summary.comparison} showLoyverseMetrics={showLoyverseSections} />
      </section>

      {/* "Actividad de hoy" vivía en el panel de pendientes del Inicio. Es telemetría del equipo,
          no una decisión del dueño: aquí es donde se consulta cuando se quiere consultar. */}
      <TodayActivitySection onNavigate={onNavigate} />
    </>}
  </div>;
}

/** Lo que el equipo registró hoy. Informativo — nunca fue un pendiente accionable. */
function TodayActivitySection({ onNavigate }: { onNavigate: (destination: string) => void }) {
  const [pending, setPending] = useState<PendingSummary>();
  useEffect(() => {
    getPendingSummary().then(setPending).catch(() => { /* informativo: su ausencia no rompe nada */ });
  }, []);
  if (!pending) return null;
  const rows = [
    { key: 'purchases', label: 'Compras', count: pending.purchasesToday, destination: 'purchases' },
    { key: 'expenses', label: 'Gastos', count: pending.expensesToday, destination: 'expenses' },
    { key: 'shifts', label: 'Cortes', count: pending.shiftsToday, destination: 'shifts' },
  ].filter((row) => row.count > 0);
  if (rows.length === 0) return null;
  return (
    <section className="preview-section owner-dashboard-section">
      <div className="preview-section__heading"><div><h3>Actividad de hoy</h3><span>Lo que tu equipo registró hoy.</span></div></div>
      <div className="consumption-summary" aria-label="Actividad de hoy">
        {rows.map((row) => (
          <article key={row.key}>
            <span>{row.label}</span>
            <strong>{row.count}</strong>
            <small><button className="link-button" onClick={() => onNavigate(row.destination)} type="button">Ver</button></small>
          </article>
        ))}
      </div>
    </section>
  );
}

// Bases de cálculo (spec §10 R1, "detalle conservado"): etiquetado obligatorio de toda cifra que
// se conserva en esta vista. Son las tres bases reales que produce el backend hoy.
const BASIS_ALL = 'Todos los canales';
const BASIS_COSTED = 'Solo Loyverse · solo líneas con costo conocido';
const BASIS_SHIFTS = 'Solo cortes de turno cerrados';

function MetricCard({ label, value, tone, help, basis }: { label: string; value: string; tone: 'orange' | 'green' | 'slate'; help?: string; basis?: string }) {
  return <article className="metric-card">
    <span className={`metric-card__icon metric-card__icon--${tone}`}>{label.charAt(0)}</span>
    <div>
      <strong>{value}</strong>
      <span title={help}>{label}</span>
      {basis && <small className="metric-card__basis">{basis}</small>}
    </div>
  </article>;
}

function SummaryCard({ label, value, help }: { label: string; value: string; help?: string }) {
  return <article><span>{label}</span><strong>{value}</strong>{help && <small className="summary-card-help">{help}</small>}</article>;
}

const ALERT_SEVERITY_LABELS: Record<DashboardAlertSeverity, string> = { CRITICAL: 'Crítica', WARNING: 'Advertencia', INFO: 'Información' };
const ALERT_SEVERITY_TONE: Record<DashboardAlertSeverity, string> = { CRITICAL: 'error', WARNING: 'warning', INFO: 'info' };
const ALERT_TYPE_LABELS: Record<DashboardAlert['type'], string> = {
  MISSING_RECIPE: 'Producto sin receta',
  INCOMPLETE_RECIPE: 'Receta incompleta',
  MISSING_REAL_COST: 'Sin costo real',
  LOW_STOCK: 'Se está acabando',
  NO_STOCK: 'Insumo agotado',
  NEGATIVE_STOCK: 'Existencia negativa',
  HIGH_COST_DIFFERENCE: 'Diferencia alta de costo',
  CASH_SHORT: 'Faltante de efectivo',
  CASH_OVER: 'Sobrante de efectivo',
  SHIFT_CLOSE_MISSING: 'Falta corte de turno',
  SALES_NOT_POSTED_TO_INVENTORY: 'Ventas no descontadas de inventario',
  REVERSED_POSTINGS: 'Descuentos revertidos',
  VOIDED_SHIFT_CLOSES: 'Cortes anulados',
};

function AlertCard({ alert }: { alert: DashboardAlert }) {
  return <article className={`alert-card alert-card--${alert.severity.toLowerCase()}`}>
    <div className="alert-card__header">
      <span className={`issue-severity issue-severity--${ALERT_SEVERITY_TONE[alert.severity]}`}>{ALERT_SEVERITY_LABELS[alert.severity]}</span>
      <strong>{alert.title}</strong>
    </div>
    <p>{alert.message}</p>
    {(alert.storeName || alert.productName || alert.ingredientName) && <div className="alert-card__meta">
      {alert.storeName && <span>Tienda: {alert.storeName}</span>}
      {alert.productName && <span>Producto: {alert.productName}</span>}
      {alert.ingredientName && <span>Insumo: {alert.ingredientName}</span>}
    </div>}
    {alert.suggestedAction && <small className="alert-card__action">{alert.suggestedAction}</small>}
    <small className="alert-card__type">{ALERT_TYPE_LABELS[alert.type] ?? 'Alerta operativa'}</small>
  </article>;
}

function StoreBreakdownTable({ rows }: { rows: DashboardStoreBreakdownItem[] }) {
  if (!rows.length) return <div className="empty-panel empty-panel--small"><strong>No hay ventas por tienda para este periodo.</strong></div>;
  // P-076: tabla analítica compacta (se comparan tiendas): identidad fija y desplazamiento local en el teléfono.
  return <div className="data-table-wrap"><table className="data-table n3-table--analytic"><thead><tr>
    <th>Tienda</th><th>Venta bruta</th><th>Tickets</th><th>Costo real promedio</th><th>Margen real promedio</th><th>Margen real %</th><th>Diferencia efectivo</th><th>Cortes activos</th><th>Alertas</th>
  </tr></thead><tbody>{rows.map((row) => <tr key={row.storeId}>
    <td>{row.storeName}</td>
    <td>{formatMoney(row.grossSales)}</td>
    <td>{row.totalReceipts}</td>
    <td>{formatOptionalMoney(row.realAverageCost)}</td>
    <td>{formatOptionalMoney(row.realAverageMargin)}</td>
    <td>{formatOptionalPercent(row.realAverageMarginPercent)}</td>
    <td>{formatOptionalMoney(row.cashDifference)} <small>{cashDifferenceLabel(row.cashDifference)}</small></td>
    <td>{row.activeCloses}</td>
    <td>{row.alertsCount}</td>
  </tr>)}</tbody></table></div>;
}

const REAL_COST_STATUS_AVAILABLE = 'AVAILABLE';

function TopProductsTable({ rows }: { rows: DashboardTopProductItem[] }) {
  if (!rows.length) return <div className="empty-panel empty-panel--small"><strong>No hay productos vendidos para este periodo.</strong></div>;
  return <div className="data-table-wrap"><table className="data-table n3-table--analytic"><thead><tr>
    <th>Producto</th><th>Variante</th><th>Cantidad vendida</th><th>Venta bruta</th><th>Costo estimado</th><th>Costo real promedio</th><th>Margen real promedio</th><th>Margen real %</th><th>Estado receta</th><th>Estado costo real</th>
  </tr></thead><tbody>{rows.map((row, index) => <tr key={index}>
    <td><strong>{row.productName}</strong></td>
    <td>{row.variantName || '—'}</td>
    <td>{formatQuantity(row.quantitySold)}</td>
    <td>{formatMoney(row.grossSales)}</td>
    <td>{formatOptionalMoney(row.estimatedCost)}</td>
    <td>{formatOptionalMoney(row.realAverageCost)}</td>
    <td>{formatOptionalMoney(row.realAverageMargin)}</td>
    <td>{formatOptionalPercent(row.realAverageMarginPercent)}</td>
    <td><RecipeStatusBadge status={row.recipeStatus} /></td>
    <td><RealCostBadge available={row.realCostStatus === REAL_COST_STATUS_AVAILABLE} /></td>
  </tr>)}</tbody></table></div>;
}

function DailyTrendTable({ rows }: { rows: DashboardDailyTrendItem[] }) {
  if (!rows.length) return <div className="empty-panel empty-panel--small"><strong>No hay ventas registradas día por día en este periodo.</strong></div>;
  const maxGrossSales = Math.max(...rows.map((row) => Number(row.grossSales)), 1);
  return <div className="data-table-wrap"><table className="data-table n3-table--analytic"><thead><tr>
    <th>Fecha</th><th>Venta bruta</th><th>Tickets</th><th>Margen real promedio</th><th>Diferencia efectivo</th><th>Salidas por venta</th>
  </tr></thead><tbody>{rows.map((row) => <tr key={row.date}>
    <td>{row.date}</td>
    <td>
      <div className="trend-bar"><div className="trend-bar__fill" style={{ width: `${Math.max(2, (Number(row.grossSales) / maxGrossSales) * 100)}%` }} /></div>
      {formatMoney(row.grossSales)}
    </td>
    <td>{row.totalReceipts}</td>
    <td>{formatOptionalMoney(row.realAverageMargin)}</td>
    <td>{formatOptionalMoney(row.cashDifference)} <small>{cashDifferenceLabel(row.cashDifference)}</small></td>
    <td>{formatMoney(row.saleOutTotal)}</td>
  </tr>)}</tbody></table></div>;
}

function ComparisonSection({ comparison, showLoyverseMetrics }: { comparison: OwnerDashboardSummary['comparison']; showLoyverseMetrics: boolean }) {
  if (!comparison) return <div className="empty-panel empty-panel--small"><strong>No hay datos suficientes para comparar con el periodo anterior.</strong><span>Activa "Comparar con periodo anterior" en los filtros.</span></div>;
  return <>
    <p className="consumption-preview-intro">Periodo anterior: {comparison.previousDateFrom} a {comparison.previousDateTo}. {trendLabel(comparison.totalSalesChangePercent)}</p>
    <div className="coverage-summary cost-summary">
      <ComparisonCard label="Cambio en venta total" changeValue={comparison.totalSalesChange} value={formatOptionalSignedMoney(comparison.totalSalesChange)} />
      <ComparisonCard label="Cambio % venta total" changeValue={comparison.totalSalesChangePercent} value={formatOptionalSignedPercent(comparison.totalSalesChangePercent)} />
      {showLoyverseMetrics && <ComparisonCard label="Cambio en margen real" changeValue={comparison.realAverageMarginChange} value={formatOptionalSignedMoney(comparison.realAverageMarginChange)} />}
      {showLoyverseMetrics && <ComparisonCard label="Cambio % margen real" changeValue={comparison.realAverageMarginChangePercent} value={formatOptionalSignedPercent(comparison.realAverageMarginChangePercent)} />}
      {showLoyverseMetrics && <ComparisonCard label="Cambio en tickets Loyverse" changeValue={comparison.receiptsChange} value={`${comparison.receiptsChange > 0 ? '+' : ''}${comparison.receiptsChange}`} />}
      {showLoyverseMetrics && <ComparisonCard label="Cambio en ticket promedio Loyverse" changeValue={comparison.averageTicketChange} value={formatOptionalSignedMoney(comparison.averageTicketChange)} />}
      <ComparisonCard label="Cambio en diferencia de efectivo" changeValue={comparison.cashDifferenceChange} value={formatOptionalSignedMoney(comparison.cashDifferenceChange)} />
    </div>
  </>;
}

function ComparisonCard({ label, value, changeValue }: { label: string; value: string; changeValue: string | number | null }) {
  return <article><span>{label}</span><strong>{value}</strong><small className="change-direction">{changeDirectionLabel(changeValue)}</small></article>;
}

function changeDirectionLabel(value: string | number | null): string {
  if (value === null) return 'No disponible';
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return 'Sin cambio';
  return amount > 0 ? 'Aumento' : 'Disminución';
}

function trendLabel(percent: number | null): string {
  if (percent === null) return 'Sin datos suficientes para comparar.';
  if (percent > 0) return 'Mejor que el periodo anterior.';
  if (percent < 0) return 'Menor que el periodo anterior.';
  return 'Igual que el periodo anterior.';
}

function formatOptionalPercent(value: number | null): string {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) return 'No disponible';
  return `${value}%`;
}

function formatOptionalSignedPercent(value: number | null): string {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) return 'No disponible';
  return `${value > 0 ? '+' : ''}${value}%`;
}

function cashDifferenceLabel(value: string | null): string {
  if (value === null) return 'No calculable';
  const amount = Number(value);
  if (amount > 0) return 'Sobrante';
  if (amount < 0) return 'Faltante';
  return 'Sin diferencia';
}

function cashDifferenceTone(value: string | null): 'orange' | 'green' | 'slate' {
  if (value === null) return 'slate';
  const amount = Number(value);
  if (amount === 0) return 'green';
  return 'orange';
}
